import axios from "@/inside/axios";
import User from "@/inside/models/User";
import CookiesConsentRepository from "@/common/models/CookiesConsentRepository";

/**
 * Initializes Intercom widget.
 * @param {User} user
 * @return {Promise}
 */
function initIntercom(user) {
    return new Promise((resolve, reject) => {
        axios.get("/my/intercom", {
            validateStatus: status => status === 200
        }).then((response) => {
            window.Intercom("boot", {
                // Non-custom attributes
                user_hash: response.data,
                app_id: "f5kjtzhr",
                email: user.email,
                phone: user.phoneNumber.landline,
                name: user.fullName,
                company: {
                    // Non-custom attributes
                    company_id: user.account.id,
                    name: user.account.name,
                    plan: user.account.type,
                    size: user.account.workforce,
                    website: user.account.website,
                    // Custom attributes
                    manager: user.account.manager.fullName
                },
                // Custom attributes
                echoline_id: user.id,
                mobile: user.phoneNumber.mobile,
                language_override: user.language,
                job_title: user.function,
                beta_tester_status: user.canAccessBeta,
                demo_account_customer: (user.account.demoEndDate !== null)
            });

            const promises = [];

            promises.push(axios.get(`/accounts/${user.account.id}/activities`, {
                validateStatus: status => status === 200
            }).then((response) => {
                window.Intercom("update", {
                    company: {
                        company_id: user.account.id,
                        industry: response.data.join(", ")
                    }
                });
            }).catch((error) => {
                const customError = new Error(`Error while fetching account activities (${error.message})`);
                customError.response = error.response;
                reject(customError);
            }));

            promises.push(axios.get(`/accounts/${user.account.id}/packages`, {
                validateStatus: status => status === 200
            }).then((response) => {
                window.Intercom("update", {
                    company: {
                        company_id: user.account.id,
                        packages: response.data.map((package_data) => {
                            return package_data.name;
                        }).join(", ")
                    }
                });
            }).catch((error) => {
                const customError = new Error(`Error while fetching account packages (${error.message})`);
                customError.response = error.response;
                reject(customError);
            }));

            promises.push(axios.get(`/accounts/${user.account.id}/contacts`, {
                validateStatus: status => status === 200
            }).then((response) => {
                window.Intercom("update", {
                    company: {
                        company_id: user.account.id,
                        interlocutors: response.data.map((contact_data) => {
                            return `${contact_data.firstname} ${contact_data.lastname}`;
                        }).join(", ")
                    }
                });
            }).catch((error) => {
                const customError = new Error(`Error while fetching account main contacts (${error.message})`);
                customError.response = error.response;
                reject(customError);
            }));

            Promise.all(promises).then(() => {
                resolve();
            });
        }).catch((error) => {
            reject(error);
        });
    });
}

export default {
    actions: {
        /**
         * Initializes module state.
         *
         * @param {function} commit
         * @return {Promise}
         */
        init({commit}) {
            return new Promise((resolve, reject) => {
                if (!window.localStorage.getItem("token")) {
                    reject();
                }

                if (!window.localStorage.getItem("cookies_consent")) {
                    reject();
                }

                axios.defaults.headers.common["Authorization"] = `Bearer ${window.localStorage.getItem("token")}`;

                axios.get("/me", {
                    validateStatus: (status => status === 200)
                }).then((response) => {
                    if (response.resource !== "customer") {
                        console.error("You are an admin, and admins cannot login to Echoline app directly!");
                        reject();
                    }

                    const user = User.fromAPI(response.data);

                    if (process.env.VUE_APP_ENVIRONMENT === "production") {
                        initIntercom(user);
                    }

                    commit("setUser", user);
                    resolve();
                }).catch((error) => {
                    reject(error);
                });
            });
        },
        /**
         * Logs user out, and redirects to outside SPA.
         */
        logout() {
            window.localStorage.removeItem("token");
            delete axios.defaults.headers.common["Authorization"];
            window.location.href = "/outside.html";
        }
    },
    getters: {
        /**
         * Returns cookies consent data from local storage.
         *
         * @returns {CookiesConsentRepository}
         */
        cookiesConsent() {
            let out = new CookiesConsentRepository();

            return (typeof window.localStorage.getItem("cookies_consent") === "string"
                ? Object.assign(out, JSON.parse(window.localStorage.getItem("cookies_consent")))
                : out
            );
        },
        /**
         * Returns current user.
         *
         * @param {object} state
         * @returns {User}
         */
        get(state) {
            return state.user;
        }
    },
    mutations: {
        /**
         * Replaces cookies consent data in local storage.
         *
         * @param {object} state
         * @param {CookiesConsentRepository} cookies_consent_repository
         */
        setCookiesConsent(state, cookies_consent_repository) {
            if (!(cookies_consent_repository instanceof CookiesConsentRepository)) {
                throw new TypeError(`VueX 'user/setCookiesConsent' mutation expects payload to be an instance of CookiesConsentRepository, ${typeof cookies_consent_repository} given`);
            }

            window.localStorage.setItem("cookies_consent", JSON.stringify(cookies_consent_repository));
        },
        /**
         * Stores given `user` in module state.
         *
         * @param {object} state
         * @param {User} user
         */
        setUser(state, user) {
            if (!(user instanceof User)) {
                throw new TypeError(`VueX 'user/setUser' mutation expects payload to be an instance of User, ${typeof user} given`);
            }

            Object.assign(state.user, user);
        }
    },
    namespaced: true,
    state: {
        user: new User()
    }
}
